import React, { useMemo, useRef, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Alert, AlertTitle, alpha, useTheme,
} from '@mui/material';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip as ChartTooltip, Legend, Filler } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, ChartTooltip, Legend, Filler);

// ============================================================================
// EQUITY CURVE
// ============================================================================
function EquityCurve({ closedTrades }) {
  const theme = useTheme();

  const chartData = useMemo(() => {
    if (!closedTrades.length) return null;

    const sorted = [...closedTrades].sort((a, b) =>
      new Date(a.closedAt || a.createdAt).getTime() - new Date(b.closedAt || b.createdAt).getTime()
    );

    let cumPnl = 0;
    let maxPnl = 0;
    const labels = [];
    const equity = [];
    const drawdown = [];

    sorted.forEach((t, i) => {
      const pnl = t.realizedPnlPercent || t.pnlPercent || 0;
      cumPnl += pnl;
      maxPnl = Math.max(maxPnl, cumPnl);
      labels.push(`#${i + 1}`);
      equity.push(parseFloat(cumPnl.toFixed(2)));
      drawdown.push(parseFloat((cumPnl - maxPnl).toFixed(2)));
    });

    return {
      labels,
      datasets: [
        {
          label: 'Cumulative P&L (%)',
          data: equity,
          borderColor: theme.palette.success.main,
          backgroundColor: alpha(theme.palette.success.main, 0.1),
          fill: true,
          tension: 0.3,
          pointRadius: 2,
          borderWidth: 2,
        },
        {
          label: 'Drawdown (%)',
          data: drawdown,
          borderColor: theme.palette.error.main,
          backgroundColor: alpha(theme.palette.error.main, 0.05),
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          borderWidth: 1,
          borderDash: [4, 2],
        },
      ],
    };
  }, [closedTrades, theme]);

  if (!chartData) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">No closed trades yet. Equity curve will appear here.</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Equity Curve & Drawdown</Typography>
      <Box sx={{ height: 250 }}>
        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { grid: { display: false }, ticks: { maxTicksLimit: 10, font: { size: 10 } } },
              y: { grid: { color: alpha(theme.palette.divider, 0.3) }, ticks: { font: { size: 10 }, callback: v => v + '%' } },
            },
            plugins: {
              legend: { display: true, position: 'top', labels: { font: { size: 10 }, boxWidth: 12 } },
              tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${ctx.raw}%` } },
            },
            interaction: { intersect: false, mode: 'index' },
          }}
        />
      </Box>
    </Paper>
  );
}

// ============================================================================
// OUTCOME DISTRIBUTION HISTOGRAM
// ============================================================================
function OutcomeDistribution({ closedTrades }) {
  const theme = useTheme();

  const chartData = useMemo(() => {
    if (!closedTrades.length) return null;

    const bins = {};
    const binSize = 0.25;
    closedTrades.forEach(t => {
      const pnl = t.realizedPnlPercent || t.pnlPercent || 0;
      const bin = Math.floor(pnl / binSize) * binSize;
      const key = bin.toFixed(2);
      bins[key] = (bins[key] || 0) + 1;
    });

    const sortedKeys = Object.keys(bins).sort((a, b) => parseFloat(a) - parseFloat(b));
    return {
      labels: sortedKeys.map(k => `${k}%`),
      datasets: [{
        label: 'Trade Count',
        data: sortedKeys.map(k => bins[k]),
        backgroundColor: sortedKeys.map(k => parseFloat(k) >= 0 ? alpha(theme.palette.success.main, 0.6) : alpha(theme.palette.error.main, 0.6)),
        borderRadius: 2,
      }],
    };
  }, [closedTrades, theme]);

  if (!chartData) return null;

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Outcome Distribution</Typography>
      <Box sx={{ height: 200 }}>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { grid: { display: false }, ticks: { font: { size: 9 }, maxRotation: 45 } },
              y: { grid: { color: alpha(theme.palette.divider, 0.3) }, ticks: { font: { size: 10 }, stepSize: 1 } },
            },
            plugins: {
              legend: { display: false },
            },
          }}
        />
      </Box>
    </Paper>
  );
}

// ============================================================================
// SMALL WINS PROBLEM INDICATOR
// ============================================================================
function SmallWinsIndicator({ closedTrades, modeInfo }) {
  const theme = useTheme();

  const analysis = useMemo(() => {
    if (closedTrades.length < 5) return null;

    const wins = closedTrades.filter(t => (t.realizedPnlPercent || t.pnlPercent || 0) > 0);
    const losses = closedTrades.filter(t => (t.realizedPnlPercent || t.pnlPercent || 0) <= 0);

    const avgWin = wins.length > 0 ? wins.reduce((s, t) => s + (t.realizedPnlPercent || t.pnlPercent || 0), 0) / wins.length : 0;
    const avgLoss = losses.length > 0 ? Math.abs(losses.reduce((s, t) => s + (t.realizedPnlPercent || t.pnlPercent || 0), 0) / losses.length) : 0;
    const winRate = (wins.length / closedTrades.length) * 100;

    const leverage = modeInfo?.leverage || 5;
    const feePerTrade = leverage >= 10 ? 0.12 : 0.06;
    const feeDrag = feePerTrade * 2;
    const netAvgWin = avgWin - feeDrag;
    const netAvgLoss = avgLoss + feeDrag;

    const breakEvenWinRate = netAvgLoss > 0 ? (netAvgLoss / (netAvgWin + netAvgLoss)) * 100 : 50;
    const isSmallWinsProblem = netAvgWin < feeDrag * 2 && winRate < 65;

    return {
      avgWin: avgWin.toFixed(3),
      avgLoss: avgLoss.toFixed(3),
      netAvgWin: netAvgWin.toFixed(3),
      feeDrag: feeDrag.toFixed(3),
      breakEvenWinRate: breakEvenWinRate.toFixed(1),
      winRate: winRate.toFixed(1),
      isSmallWinsProblem,
      profitFactor: netAvgLoss > 0 ? ((wins.length * netAvgWin) / (losses.length * netAvgLoss)).toFixed(2) : '∞',
    };
  }, [closedTrades, modeInfo]);

  if (!analysis) return null;

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: analysis.isSmallWinsProblem ? 'warning.main' : 'divider', borderRadius: 2, bgcolor: analysis.isSmallWinsProblem ? alpha(theme.palette.warning.main, 0.03) : 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {analysis.isSmallWinsProblem ? <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main' }} /> : <LightbulbIcon sx={{ fontSize: 18, color: 'info.main' }} />}
        <Typography variant="subtitle2" fontWeight="bold">
          {analysis.isSmallWinsProblem ? 'Small Wins Problem Detected' : 'Win/Loss Analysis'}
        </Typography>
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Avg Win</Typography>
          <Typography variant="body2" fontWeight={600} color="success.main">+{analysis.avgWin}%</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Avg Loss</Typography>
          <Typography variant="body2" fontWeight={600} color="error.main">-{analysis.avgLoss}%</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Fee Drag</Typography>
          <Typography variant="body2" fontWeight={600}>{analysis.feeDrag}%</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">BE Win Rate</Typography>
          <Typography variant="body2" fontWeight={600}>{analysis.breakEvenWinRate}%</Typography>
        </Grid>
      </Grid>

      {analysis.isSmallWinsProblem && (
        <Alert severity="warning" variant="outlined" sx={{ mt: 1.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
          Avg win ({analysis.netAvgWin}% net) is too small after fees. Consider:
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <li>Switch to Mode 2 (Trend/Hybrid) for bigger targets</li>
            <li>Enable Multi-Target TP for partial exits at better levels</li>
            <li>Enable Adaptive Trailing to let winners run longer</li>
          </Box>
        </Alert>
      )}
    </Paper>
  );
}

// ============================================================================
// MAIN TAB
// ============================================================================
export default function PerformanceTab({ strategyId, positionsData, scheduleData, modeInfo }) {
  const closedTrades = useMemo(() =>
    (positionsData || []).filter(p => p.status === 'closed' || p.closedAt),
    [positionsData]
  );

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <EquityCurve closedTrades={closedTrades} />
        </Grid>
        <Grid item xs={12} md={7}>
          <OutcomeDistribution closedTrades={closedTrades} />
        </Grid>
        <Grid item xs={12} md={5}>
          <SmallWinsIndicator closedTrades={closedTrades} modeInfo={modeInfo} />
        </Grid>
      </Grid>
    </Box>
  );
}
